import { useMemo } from 'react';
import { useToggle } from 'react-use';

import { Pesel } from '@dreryk/personal-external-identifier-common-api';

import { Loader, LoaderSize, Modal, ModalWidth } from '@dreryk/react-components';

import { guardiansTranslationKeys } from '../../translations';

import { GuardianDetailsFormBean } from './GuardianDetailsModal/GuardianDetailsModal';
import { GuardianModalFormBean, StatutoryRepresentative } from './GuardianModal.types';
import { GuardianModalForm, GuardianModalFormProps } from './GuardianModalForm';
import { useGuardianQuery } from './PatientAutosuggest/GuardianModal.hooks';

type GuardianIdProp = { readonly guardianId: number };
type GuardianModalProps = {
   readonly onClose: VoidFunction;
   readonly statutoryRepresentative?: boolean;
   readonly onSubmit?: (formData: GuardianModalFormBean) => void;
   readonly patientId?: number;
   readonly patientPesel: Pesel | null | undefined;
   readonly initialData?: GuardianModalFormBean;
   readonly isIdentifyingGuardian?: boolean;
   onSuccess?: (parameters: GuardianModalFormBean) => void;
} & Partial<GuardianIdProp>;

// eslint-disable-next-line max-lines-per-function
const GuardianModalEditable = (props: GuardianModalFormProps & GuardianIdProp): JSX.Element => {
   const { data: guardian, loading } = useGuardianQuery(props.guardianId);
   const initialFormValues = useMemo<GuardianModalFormBean | undefined>(() => {
      if (!guardian) {
         return undefined;
      }
      return {
         fullName: `${guardian.lastName} ${guardian.firstName}`,
         relationCode: guardian.relation,
         statutoryRepresentative: guardian.statutoryRepresentative
            ? StatutoryRepresentative.YES
            : StatutoryRepresentative.NO,
         guardian: {
            firstName: guardian.firstName.asString(),
            middleName: guardian.middleName?.asString() ?? null,
            lastName: guardian.lastName.asString(),
            gender: null,
            ...guardian.externalIdentifier.accept<
               Pick<
                  GuardianDetailsFormBean,
                  | 'guardianExternalIdentifierType'
                  | 'guardianExternalIdentifierValue'
                  | 'passportCountry'
                  | 'passportValidTo'
                  | 'nationalIdentificationNumberCountry'
               >
            >({
               visitPesel: (pesel) => ({
                  guardianExternalIdentifierType: 'Pesel',
                  guardianExternalIdentifierValue: pesel.asString(),
                  passportCountry: null,
                  passportValidTo: null,
                  nationalIdentificationNumberCountry: null,
               }),
               visitPassport: (passport) => ({
                  guardianExternalIdentifierType: 'Passport',
                  guardianExternalIdentifierValue: passport.getValue(),
                  passportCountry: passport.getCountry(),
                  passportValidTo: passport.getValidTo(),
                  nationalIdentificationNumberCountry: null,
               }),
               visitNationalIdentificationNumber: (nationalIdentificationNumber) => ({
                  guardianExternalIdentifierType: 'NationalIdentificationNumber',
                  guardianExternalIdentifierValue: nationalIdentificationNumber.getValue(),
                  nationalIdentificationNumberCountry: nationalIdentificationNumber.getCountry(),
                  passportCountry: null,
                  passportValidTo: null,
               }),
            }),
            country: guardian.address.getCountry(),
            flat: guardian.address.findFlat()?.asString() ?? null,
            house: guardian.address.findBuilding()?.asString() ?? null,
            postcode: guardian.address.findPostcode() ?? null,
            street: guardian.address.findStreet()?.asString() ?? null,
            town: guardian.address.findTown()?.getName() ?? null,
            emailAddress: guardian.emailAddress ?? null,
            phoneNumber: guardian.phoneNumber ?? null,
         },
      };
   }, [guardian]);
   if (loading) {
      return <Loader size={LoaderSize.MEDIUM} testId={'loader'} />;
   }
   return (
      <GuardianModalForm
         patientId={props.patientId}
         patientPesel={props.patientPesel}
         initialValues={initialFormValues}
         onGuardianDetailsToggleVisibility={props.onGuardianDetailsToggleVisibility}
         onCancel={props.onCancel}
         onSubmit={props.onSubmit}
         isIdentifyingGuardian={props.isIdentifyingGuardian}
         statutoryRepresentative={props.statutoryRepresentative}
         guardianId={props.guardianId}
         onSuccess={props.onSuccess}
      />
   );
};

const GuardianModal = (props: GuardianModalProps): JSX.Element => {
   const [visible, toggleVisibility] = useToggle(true);
   const { guardianId } = props;

   return (
      <Modal
         visible={visible}
         title={props.guardianId ? guardiansTranslationKeys.editGuardian : guardiansTranslationKeys.addGuardian}
         width={ModalWidth.WIDE}
         testId={'guardian'}
         shouldCloseOnOverlayClick={false}
         shouldCloseOnEsc={false}
      >
         {guardianId ? (
            <GuardianModalEditable
               patientId={props.patientId}
               patientPesel={props.patientPesel}
               guardianId={guardianId}
               onGuardianDetailsToggleVisibility={toggleVisibility}
               statutoryRepresentative={props.statutoryRepresentative}
               onCancel={props.onClose}
               isIdentifyingGuardian={props.isIdentifyingGuardian}
               onSuccess={props.onSuccess}
            />
         ) : (
            <GuardianModalForm
               patientId={props.patientId}
               patientPesel={props.patientPesel}
               onSubmit={props.onSubmit}
               onGuardianDetailsToggleVisibility={toggleVisibility}
               statutoryRepresentative={props.statutoryRepresentative}
               initialValues={props.initialData}
               onCancel={props.onClose}
               isIdentifyingGuardian={props.isIdentifyingGuardian}
               onSuccess={props.onSuccess}
            />
         )}
      </Modal>
   );
};

export { GuardianModal };
